import {useEffect, useState} from "react";
import classNames from "classnames";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faPencil,
    faTrashCan
} from "@fortawesome/free-solid-svg-icons";

import {defaultNewSection, TEXT} from "../../utils/constants";

import CustomSwitch from "../elements/CustomSwitch";
import InputField from "../elements/InputField";
import ButtonLink from "../elements/ButtonLink";
import Button from "../elements/Button";

const SectionEditForm = ({
                             courseId,
                             initial = null,
                             onCancel,
                             onSave
                         }) => {
    const isNew = !initial || !initial.id;

    const buildInitialForm = () => {
        if (!initial) {
            return {
                ...defaultNewSection(courseId),
                _originalId: null,
                contents: []
            };
        }

        return {
            ...initial,

            // IMPORTANT:
            // preserve original id even if edited
            _originalId: initial.id,

            contents: (initial.contents || []).map((c, idx) => ({
                ...c,
                position: c.position ?? idx + 1,
                _isNew: false,
                _deleted: false
            }))
        };
    };

    const [form, setForm] = useState(buildInitialForm);

    const [activeTab, setActiveTab] = useState("general");

    const [loadingSave, setLoadingSave] = useState(false);

    useEffect(() => {
        setForm(buildInitialForm());
        setActiveTab("general");
    }, [initial, courseId]);

    // =========================================
    // CONTENT FILTERS
    // =========================================

    const existingContents = (form.contents || []).filter(
        (c) => !c._isNew && !c._deleted
    );

    const newContents = (form.contents || []).filter(
        (c) => c._isNew && !c._deleted
    );

    const deletedExisting = (form.contents || []).filter(
        (c) => !c._isNew && c._deleted
    );

    // =========================================
    // CONTENT ACTIONS
    // =========================================

    const addContent = () => {
        setForm((prev) => ({
            ...prev,
            contents: [
                ...(prev.contents || []),
                {
                    id: "",
                    type: 0,
                    content: {},
                    is_active: true,

                    _isNew: true,
                    _deleted: false,

                    _tempKey: `tmp-${Date.now()}-${Math.random()}`
                }
            ]
        }));
    };

    const updateNewContentField = (tempKey, key, value) => {
        setForm((prev) => ({
            ...prev,
            contents: (prev.contents || []).map((c) =>
                c._tempKey === tempKey
                    ? {
                        ...c,
                        [key]: value
                    }
                    : c
            )
        }));
    };

    const removeNewContent = (tempKey) => {
        setForm((prev) => ({
            ...prev,
            contents: (prev.contents || []).filter(
                (c) => c._tempKey !== tempKey
            )
        }));
    };

    const toggleDeleteExistingContent = (id) => {
        setForm((prev) => ({
            ...prev,
            contents: (prev.contents || []).map((c) =>
                String(c.id) === String(id)
                    ? {
                        ...c,
                        _deleted: !c._deleted
                    }
                    : c
            )
        }));
    };

    // =========================================
    // VALIDATION
    // =========================================

    const validateForm = () => {
        const sectionId = String(form.id || "").trim();

        if (!sectionId) {
            return "شناسه سرفصل نمی‌تواند خالی باشد.";
        }

        for (const c of newContents) {
            const contentId = String(c.id || "").trim();

            if (!contentId) {
                return "همه‌ی محتواهای جدید باید شناسه داشته باشند.";
            }

            const type = Number(c.type);

            if (![0, 1, 2].includes(type)) {
                return "نوع محتوا فقط می‌تواند 0 یا 1 یا 2 باشد.";
            }
        }

        return null;
    };

    // =========================================
    // SAVE
    // =========================================

    const saveSection = async () => {

        const error = validateForm();

        if (error) {
            console.log("VALIDATION ERROR", error);

            alert(error);
            return;
        }

        setLoadingSave(true);

        try {

            await onSave({
                ...form,
                contents: [...(form.contents || [])]
            });

        } catch (err) {
            console.error("SAVE ERROR", err);

            alert(
                err?.response?.data?.message ||
                err?.message ||
                "خطا در ذخیره"
            );
        } finally {
            setLoadingSave(false);
        }
    };

    // =========================================
    // RENDER
    // =========================================

    return (
        <div className="space-y-6 text-right">
            {/* Tabs */}

            <div className="flex gap-2 border-b pb-2">
                {[
                    {
                        id: "general",
                        label: TEXT.sectionSettings
                    },
                    {
                        id: "contents",
                        label: TEXT.contentsHeader
                    }
                ].map((tab) => (
                    <Button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={classNames(
                            "px-4 py-2 rounded-t-md border border-b-0 text-sm",
                            activeTab === tab.id
                                ? "bg-white text-black border-gray-300"
                                : "bg-gray-100 text-gray-600 border-transparent"
                        )}
                    >
                        <span>{tab.label}</span>
                    </Button>
                ))}
            </div>

            {/* GENERAL */}

            {activeTab === "general" && (
                <div className="bg-gray-50 border rounded-lg p-5 space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <h5 className="text-sm font-semibold text-gray-600">
                            {TEXT.sectionSettings}
                        </h5>

                        <CustomSwitch
                            checked={!!form.is_active}
                            onChange={(next) =>
                                setForm((prev) => ({
                                    ...prev,
                                    is_active: next
                                }))
                            }
                            label={
                                form.is_active
                                    ? TEXT.switch.active
                                    : TEXT.switch.inactive
                            }
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-gray-600">
                            {TEXT.labels.id}
                        </label>

                        <InputField
                            value={form.id || ""}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    id: e.target.value
                                }))
                            }
                            className="w-full border rounded p-2 text-right"
                        />
                    </div>
                </div>
            )}

            {/* CONTENTS */}

            {activeTab === "contents" && (
                <div className="bg-gray-50 border rounded-lg p-5 space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <h5 className="text-sm font-semibold text-gray-600">
                            {TEXT.contentsHeader}
                        </h5>

                        <Button
                            type="button"
                            onClick={addContent}
                            className="px-4 py-2 border rounded-md text-sm"
                        >
                            <span>{TEXT.addContent}</span>
                        </Button>
                    </div>

                    {/* Existing */}

                    {(existingContents.length > 0 ||
                        deletedExisting.length > 0) && (
                        <div className="space-y-2">
                            {existingContents.map((c) => (
                                <div
                                    key={c.id}
                                    className="flex justify-between items-center border rounded p-2 bg-white"
                                >
                                    <div>
                                        <div className="text-sm font-medium">
                                            {c.id || "بدون عنوان"}
                                        </div>

                                        <div className="text-xs text-gray-500">
                                            type: {c.type} • position: {c.position}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <ButtonLink
                                            to={`/admin/course/${courseId}/content/${c.id}`}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPencil}
                                            />
                                        </ButtonLink>

                                        <Button
                                            onClick={() =>
                                                toggleDeleteExistingContent(
                                                    c.id
                                                )
                                            }
                                            className="p-2 rounded border border-red-200 text-red-600 hover:bg-red-50"
                                        >
                                            <FontAwesomeIcon
                                                icon={faTrashCan}
                                            />
                                        </Button>
                                    </div>
                                </div>
                            ))}

                            {deletedExisting.map((c) => (
                                <div
                                    key={`deleted-${c.id}`}
                                    className="text-xs text-red-600"
                                >
                                    برای حذف علامت‌گذاری شد: {c.id}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* New */}

                    {newContents.length === 0 ? (
                        <p className="text-sm text-gray-500">
                            {TEXT.noContents}
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {newContents.map((c) => (
                                <div
                                    key={c._tempKey}
                                    className="flex flex-col md:flex-row gap-3 md:items-center border rounded-lg p-3 bg-white"
                                >
                                    <div className="flex-1 flex items-center gap-2">
                                        <InputField
                                            value={c.id || ""}
                                            title="ID"
                                            onChange={(e) =>
                                                updateNewContentField(
                                                    c._tempKey,
                                                    "id",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full border rounded p-2 text-right"
                                        />

                                        <InputField
                                            value={c.type ?? 0}
                                            type="number"
                                            min={0}
                                            max={2}
                                            title="Type"
                                            onChange={(e) =>
                                                updateNewContentField(
                                                    c._tempKey,
                                                    "type",
                                                    Number(e.target.value)
                                                )
                                            }
                                            className="w-full border rounded p-2 text-right"
                                        />

                                        <Button
                                            onClick={() =>
                                                removeNewContent(c._tempKey)
                                            }
                                            className="p-2 rounded border border-red-200 text-red-600 hover:bg-red-50"
                                        >
                                            <FontAwesomeIcon
                                                icon={faTrashCan}
                                            />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ACTIONS */}

            <div className="flex justify-end gap-3 pt-2">
                <Button
                    onClick={onCancel}
                    className="px-4 py-2 border rounded"
                >
                    <span>{TEXT.buttons.cancel}</span>
                </Button>

                <Button
                    onClick={saveSection}
                    disabled={loadingSave}
                    className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                >
                    <span>
                        {loadingSave
                            ? "در حال ذخیره…"
                            : isNew
                                ? TEXT.buttons.saveNew
                                : TEXT.buttons.save}
                    </span>
                </Button>
            </div>
        </div>
    );
};

export default SectionEditForm;
